/**
 * Show a list of services.  Used in the Subscriptions tab of the Customer page.
 */
import React, { Component } from 'react';
import graphql from 'babel-plugin-relay/macro';
import { QueryRenderer } from 'react-relay';
import { Redirect } from 'react-router-dom';
import AbEnvironment from '../auth/AbEnvironment';
import PropTypes from 'prop-types';
import withStyles from '@mui/styles/withStyles';
import { Button, FormControlLabel, Grid, Link,
         Switch, TableContainer, Table,  TableBody, TableCell, TableHead, TableRow, TablePagination, Typography} from '@mui/material';
import { Add, Cancel, CheckCircle, HourglassEmpty, PauseCircleFilled } from '@mui/icons-material';

import LoadingSpinner from '../ui/LoadingSpinner';
import ServerError from '../ui/ServerError';
import { green, red, blue, amber } from '@mui/material/colors';
import { getDayLabel } from '../../utils/InputUtils';

const abEnvironment = new AbEnvironment();

const ServicesQuery = graphql`
query ServicesQuery($accountId: UUID!, $activeOnly: Int, $excludeOneTimeAccepted: Int, $first: Int, $offset: Int) {
  servicesearch(accountid: $accountId, activeonly: $activeOnly, excludeonetimeaccepted: $excludeOneTimeAccepted, first: $first, offset: $offset) {
      totalCount 
      nodes {
        customerServiceId
        appId 
        serviceStatus
        soAccepted
        appSkuPrice
        billingFrequency
        billingDayOfMonth
        appName
        appSku
        description
    } 
  }
}
`;

const styleSheet = theme => ({
    root: {
      flexGrow: 1,
      // marginTop: -32,
    },
    flex: {
      flex: 1,
    },
    divider: {
      marginTop: 4,
      marginBottom: 4,
      marginLeft: 0,
    },
    button: {
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1),
    },
    dateField: {
      marginLeft: theme.spacing(1),
      marginRight: theme.spacing(1),
    },
    textField: {
      marginLeft: theme.spacing(1),
      marginRight: theme.spacing(1),
      marginTop: 4,
      width: 180,
    },
    textField2: {
      marginLeft: theme.spacing(1),
      marginRight: theme.spacing(1),
      marginTop: 3,
      width: 160,
    },
      paper1: {
      padding: 16,
      textAlign: 'center',
      color: theme.palette.text.secondary,
    },
    paper2: {
      padding: 12,
      textAlign: 'left',
      color: theme.palette.text.secondary,
    },
    paper3: {
      padding: 16,
      textAlign: 'center',
      color: theme.palette.text.secondary,
    },
    topButton: {
      marginTop: 15
    },
    paper: {
      width: '100%',
      marginTop: theme.spacing(1),
      overflow: 'auto',
    },
    tblHead: {
      backgroundColor: '#f1f1f1',
    },
    tblHeadRow: {
      height: 32,
    },
    tblHeadCell: {
      textAlign: 'left',
      verticalAlign: 'bottom',
      borderBottom: 0,
    },
    tblHeadCellC: {
      textAlign: 'center',
      verticalAlign: 'bottom',
      borderBottom: 0,
    },

});

class Services extends Component {

  constructor(props) {
    super(props);

    this.state = {
      customerAccountId: this.props.customerAccountId,
      appName:           '', 
      activeOnly:        0,
      page:              0,
      rowsPerPage:       10
    };  
  }

  handleChange = name => event => {
    this.setState({
      [name]: event.target.value,
      page: 0,
    });
  };

  handleChangePage = (event, page) => {
    this.setState({page: page});
  }

  handleChangeRowsPerPage = (event) => {
    this.setState({ rowsPerPage: event.target.value, page : 0});
  }

  goTo(route) {
    this.props.history.push(`/${route}`);
  }

  setParentTab = (tv) => {
    if (this.props.setTabVal) {
      this.props.setTabVal(tv);
    }
  }

  getBillingDayLabel(billingDayOfMonth) {

    if (billingDayOfMonth === 32) { 
      return '(billed on the last day of month)'; 
    } 

    return '(billed on the ' + getDayLabel(billingDayOfMonth) + ' day of the month)';
  }

  goToSO() {
    let propsToCache = {
      accountId: this.props.customerAccountId,
      companyName: this.props.customerName,
      contactLastName: this.props.contactLastName, 
      contactFirstName: this.props.contactFirstName, 
      contactEmail: this.props.contactEmail
    };
    localStorage.setItem('soCustomerParms', JSON.stringify(propsToCache));

    this.props.history.push(`/subscriptionorder`);
  }

  render() {
    const { isAuthenticated, needAssent, logError } = this.props.auth;
    const classes = this.props.classes;

    if (!isAuthenticated() || needAssent()) { 
      return ( <Redirect to='/home' /> );
    }    

    const statusOk = green[300];
    const statusPause = blue[300];
    const statusWaiting = amber[400];
    const statusCancel = red[300];

    // Determine the offset
    let offset = 0;
    if (this.state.page > 0)
    {
      offset = this.state.page * this.state.rowsPerPage
    }
    let first = this.state.rowsPerPage;

    let activeOnly = null;
    if (this.state.activeOnly === 0)
    {
      //user wants to only see active services
      activeOnly = 1;
    }

    // When called from CustomerAppList, props.pg will be set to "myApps"
    let serviceBillingActive = false;
    let thePage = this.props.pg;
    if (thePage == null) {
      thePage = "custServices";

      // Should services be addable?
      let serviceBillingActive = false;
      let serviceBilling = abEnvironment.getFlagValue("serviceBilling");
      if (serviceBilling != null && serviceBilling.flagValue === 1) {
        serviceBillingActive = true;
      }
    }

    return (
      <div className={classes.root}>
        <Grid container spacing={1} direction="row">
          {thePage === "custServices" &&
          <Grid item xs={12}>
            <Typography variant="h6">
              Services
            </Typography>
          </Grid>}
          
          <Grid item xs={12}>
            <Grid container spacing={2} direction="row" >
              <Grid item>
                <FormControlLabel
                  control={
                    <Switch
                      checked={this.state.activeOnly === 0}
                      onChange={(e, checked) => {
                        if (checked) {
                          this.setState({ activeOnly: 0, page: 0});
                        } else {
                          this.setState({ activeOnly: 1, page: 0});
                        }
                      }}
                  />
                  }
                  label="Only show active services"
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <QueryRenderer
          environment={abEnvironment.getEnvironment()}
          query={ServicesQuery}
          variables={{accountId: this.state.customerAccountId, 
                      activeOnly: activeOnly,
                      excludeOneTimeAccepted: activeOnly,
                      first: first,
                      offset: offset}}
          render={({error, props})=> {
            if (error) {
              logError(error.message);
              return (
                <ServerError/>
              );
            } else if (props) {

              let serviceCount = 0;
              let totalCount = props.servicesearch.totalCount;
              let services = props.servicesearch.nodes;

              return (
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <TableContainer>
                      <Table>
                        <TableHead className={classes.tblHead}>
                          <TableRow className={classes.tblHeadRow}>
                            <TableCell className={classes.tblHeadCellLeft}>Name</TableCell>
                            <TableCell className={classes.tblHeadCellLeft}>Description</TableCell>
                            <TableCell className={classes.tblHeadCellLeft}>Amount</TableCell>
                            <TableCell className={classes.tblHeadCellLeft}>Frequency</TableCell>
                            <TableCell className={classes.tblHeadCellC}>Status</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          { services.map(n => {
                            serviceCount++;

                            let serviceName = n.appName;
                            let amount = Number(n.appSkuPrice)
                                  .toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});
                    
                            let frequency = 'One-time';
                            if (n.billingFrequency === 1) {
                              frequency  = 'Monthly ' + this.getBillingDayLabel(n.billingDayOfMonth);
                            } else if (n.billingFrequency === 2) {
                              frequency = 'Annually';
                            }


                            let serviceLink = 'servicemanage?customerServiceId=' + n.customerServiceId + '&fp=Subscriptions';

                            return (
                              <TableRow className={classes.tblHeadRow} key={serviceCount}>
                                {this.props.pg === "myApps" &&
                                <TableCell className={classes.tblHeadCellLeft}>{serviceName}</TableCell>
                              }
                                {this.props.pg !== "myApps" &&
                                  <TableCell className={classes.tblHeadCellLeft}>
                                    <Link component="button" 
                                          variant="body2"
                                          onClick={this.goTo.bind(this, serviceLink)}>
                                      {serviceName}
                                    </Link>
                                  </TableCell>
                                }

                                <TableCell className={classes.tblHeadCellLeft}>{n.description}</TableCell>
                                <TableCell >{amount}</TableCell>
                                <TableCell className={classes.tblHeadCellLeft}>{frequency}</TableCell>
                                <TableCell align="center">
                                  { n.serviceStatus === 0  && n.soAccepted === 1 &&
                                    <CheckCircle htmlColor={statusOk}/>
                                  }
                                  { n.serviceStatus === 0  && n.soAccepted === 0 &&
                                    <HourglassEmpty htmlColor={statusWaiting}/>
                                  }
                                { n.serviceStatus === 1 &&
                                    <PauseCircleFilled htmlColor={statusPause}/>
                                  }
                                  { n.serviceStatus === 2 &&
                                    <Cancel htmlColor={statusCancel}/>
                                  }
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>
                  <Grid item xs={12}>
                    <Grid container spacing={0} direction="row-reverse" justifyContent="space-between" alignItems="center">
                      <Grid item>
                        <TablePagination
                          rowsPerPageOptions={[5, 10, 25]}
                          component="div"
                          count={totalCount}
                          rowsPerPage={this.state.rowsPerPage}
                          page={this.state.page}
                          backIconButtonProps={{
                            'aria-label': 'Previous Page',
                          }}
                          nextIconButtonProps={{
                            'aria-label': 'Next Page',
                          }}
                          onPageChange={this.handleChangePage}
                          onRowsPerPageChange={this.handleChangeRowsPerPage}
                        />
                      </Grid>
                      {serviceBillingActive === true &&
                      <Grid item>
                        <Button
                            color="primary"
                            variant="contained"
                            disabled={this.props.accountStatus === 1}
                            onClick={() => {
                              this.goToSO(); 
                            }}>
                            <Add />&nbsp;Add a service
                        </Button>      
                      </Grid>}
                    </Grid>
                  </Grid>
                </Grid>
              )
            }
            return (
              <LoadingSpinner />
            );
          }}
        />
      </div>
    );
  }
}

Services.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styleSheet)(Services);
